import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Edit,
  FlaskConical,
  Loader2,
  Play,
  XCircle,
} from 'lucide-react';
import { useToast } from '../components/Toast';
import {
  getTestCase,
  getInstances,
  INSTANCE_STATUS_BADGE,
  PRIORITY_BADGE,
  STATUS_BADGE,
  TEST_ACTORS,
  TEST_MODULES,
  TestCase,
  TestCaseInstance,
} from '../services/testCasesApi';

export function TestCaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast, ToastComponent } = useToast();

  const [tc, setTc] = useState<TestCase | null>(null);
  const [instances, setInstances] = useState<TestCaseInstance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const [tcData, instData] = await Promise.all([
          getTestCase(id),
          getInstances({ testCaseId: id }),
        ]);
        setTc(tcData);
        setInstances(instData);
      } catch (err: any) {
        showToast(err.message || 'Erreur lors du chargement', 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!tc) {
    return (
      <div className="text-center py-20 text-gray-500">Cas de test introuvable.</div>
    );
  }

  const moduleName = TEST_MODULES.find((m) => m.id === tc.moduleId)?.label ?? tc.moduleId;
  const actorName = TEST_ACTORS.find((a) => a.id === tc.primaryActor)?.label ?? tc.primaryActor;

  const passRate = instances.length
    ? Math.round((instances.filter((i) => i.status === 'PASSED').length / instances.length) * 100)
    : null;

  const instanceStatusLabel: Record<string, string> = {
    NOT_RUN: 'Non exécuté',
    IN_PROGRESS: 'En cours',
    PASSED: 'Réussi',
    FAILED: 'Échoué',
    PARTIAL: 'Partiel',
    BLOCKED: 'Bloqué',
  };

  return (
    <div className="space-y-5">
      <ToastComponent />

      {/* ── Header bar ── */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <button onClick={() => navigate('/test-cases')} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 mt-0.5">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{tc.moduleId}</span>
              {tc.externalRef && <span className="text-xs font-mono text-gray-400">{tc.externalRef}</span>}
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_BADGE[tc.status]}`}>
                {tc.status === 'DRAFT' ? 'Brouillon' : tc.status === 'ACTIVE' ? 'Actif' : 'Déprécié'}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_BADGE[tc.priority]}`}>
                {tc.priority === 'CRITICAL' ? 'Critique' : tc.priority === 'HIGH' ? 'Haute'
                  : tc.priority === 'MEDIUM' ? 'Moyenne' : 'Basse'}
              </span>
            </div>
            <h1 className="text-xl font-display font-bold text-textPrimary flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-primary-600 flex-shrink-0" />
              {tc.name}
            </h1>
            {tc.description && <p className="text-sm text-gray-500 mt-0.5 leading-snug">{tc.description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => navigate(`/test-cases/${id}/edit`)} className="btn-secondary inline-flex items-center gap-2 text-sm">
            <Edit className="w-4 h-4" />
            Modifier
          </button>
          <button onClick={() => navigate(`/test-cases/${id}/execute`)} className="btn-primary inline-flex items-center gap-2 text-sm">
            <Play className="w-4 h-4" />
            Nouvelle exécution
          </button>
        </div>
      </div>

      {/* ── Stats strip ── */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-6 gap-6 text-sm">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Module</p>
            <p className="font-medium text-gray-900 truncate">{moduleName}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Acteur principal</p>
            <p className="font-medium text-gray-900 truncate">{actorName}</p>
          </div>
          {tc.secondaryActors?.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Acteurs secondaires</p>
              <p className="font-medium text-gray-900 text-xs">{tc.secondaryActors.join(', ')}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Étapes</p>
            <p className="font-bold text-lg text-gray-900">{tc.steps.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Exécutions</p>
            <p className="font-bold text-lg text-gray-900">{instances.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Taux de réussite</p>
            <p className={`font-bold text-lg ${passRate === null ? 'text-gray-400' : passRate >= 70 ? 'text-green-600' : 'text-red-600'}`}>
              {passRate === null ? '—' : `${passRate}%`}
            </p>
          </div>
        </div>
        {tc.tags.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-1.5">
            {tc.tags.map((tag) => (
              <span key={tag} className="text-xs bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">{tag}</span>
            ))}
          </div>
        )}
      </div>

      {/* ── Main body: steps (left 2/3) + conditions + history (right 1/3) ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5 items-start">

        {/* Steps — main column */}
        <div className="xl:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">📋 Étapes du test</h3>
          {tc.steps.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Aucune étape définie.</p>
          ) : (
            <div className="space-y-0 divide-y divide-gray-100">
              {tc.steps.map((step) => (
                <div key={step.order} className="grid grid-cols-[2rem_1fr_1fr] gap-4 py-3 text-sm">
                  <span className="font-mono text-xs text-gray-400 pt-0.5">{step.order}</span>
                  <p className="text-gray-800 leading-snug">{step.action}</p>
                  <p className="text-gray-500 leading-snug">{step.expectedResult}</p>
                </div>
              ))}
              <div className="grid grid-cols-[2rem_1fr_1fr] gap-4 pt-2">
                <span />
                <p className="text-xs text-gray-400 uppercase tracking-wide">Action / Navigation</p>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Résultat attendu</p>
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar: conditions + history */}
        <div className="xl:col-span-1 space-y-4">

          {/* Pre-conditions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">✅ Pré-conditions</h3>
            {tc.preconditions.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Aucune</p>
            ) : (
              <ol className="space-y-2">
                {tc.preconditions.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-gray-400 font-mono text-xs mt-0.5 flex-shrink-0">{i + 1}.</span>
                    <span className="leading-snug">{item}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Post-conditions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-800 mb-3">🏁 Post-conditions</h3>
            {tc.postconditions.length === 0 ? (
              <p className="text-sm text-gray-400 italic">Aucune</p>
            ) : (
              <ol className="space-y-2">
                {tc.postconditions.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-gray-400 font-mono text-xs mt-0.5 flex-shrink-0">{i + 1}.</span>
                    <span className="leading-snug">{item}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* Execution history */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-800">🧪 Exécutions ({instances.length})</h3>
              <button
                onClick={() => navigate(`/test-cases/${id}/execute`)}
                className="btn-secondary text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5"
              >
                <Play className="w-3.5 h-3.5" />
                Lancer
              </button>
            </div>

            {instances.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <FlaskConical className="w-7 h-7 mx-auto mb-2 text-gray-300" />
                <p className="text-xs">Aucune exécution enregistrée.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {instances.map((inst) => (
                  <div
                    key={inst.id}
                    onClick={() => navigate(`/test-case-instances/${inst.id}`)}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex-shrink-0">
                      {inst.status === 'PASSED' ? (
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      ) : inst.status === 'FAILED' ? (
                        <XCircle className="w-4 h-4 text-red-500" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${INSTANCE_STATUS_BADGE[inst.status]}`}>
                          {instanceStatusLabel[inst.status]}
                        </span>
                        <span className="text-xs text-gray-400">{inst.environment}</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">
                        {inst.date ?? ''}
                        {inst.executorName ? ` · ${inst.executorName}` : ''}
                        {inst.issues.length > 0 ? ` · ${inst.issues.length} incident(s)` : ''}
                      </p>
                    </div>
                    <ArrowLeft className="w-3.5 h-3.5 text-gray-400 rotate-180 flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
